import { LayoutService } from '@/app/layout/service/layout.service';
import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnDestroy, computed, effect, inject, input, signal } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { TooltipModule } from 'primeng/tooltip';

@Component({
    selector: '[app-menuitem]',
    imports: [CommonModule, RouterModule, TooltipModule, TranslateModule],
    template: `
        @if (root() && isVisible()) {
            <div class="layout-menuitem-root-text">{{ item().labelKey ? (item().labelKey | translate) : item().label }}</div>
        }
        @if ((!hasRouterLink() || hasChildren()) && isVisible()) {
            <a
                [attr.href]="item().url"
                (click)="itemClick($event)"
                (mouseenter)="onMouseEnter()"
                [ngClass]="item().class"
                [attr.target]="item().target"
                [attr.role]="root() ? 'menuitem' : null"
                [attr.aria-haspopup]="root() && hasChildren() ? 'true' : null"
                [attr.aria-expanded]="root() && hasChildren() ? isActive() : null"
                [attr.aria-label]="item().labelKey ? (item().labelKey | translate) : item().label"
                [attr.aria-disabled]="isDisabled() ? 'true' : null"
                [attr.tabindex]="isDisabled() ? -1 : 0"
                pRipple
                [pTooltip]="item().labelKey ? (item().labelKey | translate) : item().label"
                [tooltipDisabled]="!(isCompact() && !isActive() && root())"
            >
                <i *ngIf="item().icon" [ngClass]="item().icon" class="layout-menuitem-icon"></i>
                <span class="layout-menuitem-text text-inherit text-start font-medium">{{ item().labelKey ? (item().labelKey | translate) : item().label }}</span>
                @if (hasChildren()) {
                    <i
                        class="pi pi-fw layout-submenu-toggler"
                        [ngClass]="isHorizontalFlyout() ? 'pi-angle-right' : 'pi-angle-down'"
                    ></i>
                }
            </a>
        }
        @if (hasRouterLink() && !hasChildren() && isVisible()) {
            <a
                (click)="itemClick($event)"
                (mouseenter)="onMouseEnter()"
                [ngClass]="item().class"
                [routerLink]="item().routerLink"
                routerLinkActive="active-route"
                [routerLinkActiveOptions]="item().routerLinkActiveOptions || { paths: 'exact', queryParams: 'ignored', matrixParams: 'ignored', fragment: 'ignored' }"
                [fragment]="item().fragment"
                [queryParamsHandling]="item().queryParamsHandling"
                [preserveFragment]="item().preserveFragment"
                [skipLocationChange]="item().skipLocationChange"
                [replaceUrl]="item().replaceUrl"
                [state]="item().state"
                [queryParams]="item().queryParams"
                [attr.target]="item().target"
                [attr.role]="root() ? 'menuitem' : null"
                [attr.aria-label]="item().labelKey ? (item().labelKey | translate) : item().label"
                [attr.aria-disabled]="isDisabled() ? 'true' : null"
                [attr.tabindex]="isDisabled() ? -1 : 0"
                [pTooltip]="item().labelKey ? (item().labelKey | translate) : item().label"
                [tooltipDisabled]="!(isCompact() && !isActive() && root())"
            >
                <i *ngIf="item().icon" [ngClass]="item().icon" class="layout-menuitem-icon"></i>
                <span class="layout-menuitem-text text-inherit text-start font-medium">{{ item().labelKey ? (item().labelKey | translate) : item().label }}</span>
                @if (hasChildren()) {
                    <i
                        class="pi pi-fw layout-submenu-toggler"
                        [ngClass]="isHorizontalFlyout() ? 'pi-angle-right' : 'pi-angle-down'"
                    ></i>
                }
            </a>
        }
        @if (hasChildren() && isVisible()) {
            <ul
                [animate.enter]="submenuEnterAnimation()"
                [animate.leave]="submenuLeaveAnimation()"
                [class.layout-root-submenulist]="root()"
                [class.layout-horizontal-submenulist]="isHorizontalFlyout()"
                [attr.role]="root() ? 'menu' : null"
            >
                @for (child of item().items; track $index) {
                    <li
                        app-menuitem
                        [item]="child"
                        [root]="false"
                        [level]="level() + 1"
                        [parentPath]="fullPath()"
                        [class]="child['badgeClass']"
                    ></li>
                }
            </ul>
        }
    `,
    host: {
        '[class.active-menuitem]': 'isActive()',
        '[class.layout-root-menuitem]': 'root()',
        '[class.layout-flyout-menuitem]': 'isHorizontalFlyout()',
        '[class.menu-item-disabled]': 'isDisabled()'
    },
    styles: [
        `
            .p-submenu-enter {
                animation: p-animate-submenu-expand 450ms cubic-bezier(0.86, 0, 0.07, 1) forwards;
                overflow: hidden;
            }

            .p-submenu-leave {
                animation: p-animate-submenu-collapse 450ms cubic-bezier(0.86, 0, 0.07, 1) forwards;
                overflow: hidden;
            }

            @keyframes p-animate-submenu-expand {
                from {
                    max-height: 0;
                }
                to {
                    max-height: 1000px;
                }
            }

            @keyframes p-animate-submenu-collapse {
                from {
                    max-height: 1000px;
                }
                to {
                    max-height: 0;
                }
            }

            /** Same size at every depth as top-level menu links (sidebar themes style root row separately). */
            .layout-menuitem-root-text,
            .layout-menuitem-text {
                text-transform: uppercase;
                letter-spacing: 0.04em;
            }

            .layout-menuitem-text {
                font-size: 1rem;
                font-weight: 500;
                line-height: 1.25rem;
            }

            /** Disabled items: visible but not navigable (includes Fase 2 placeholders). */
            :host(.menu-item-disabled) > a {
                cursor: not-allowed;
                opacity: 0.55;
                pointer-events: none;
            }

            :host(.menu-item-f2) .layout-menuitem-text::after {
                content: 'F2';
                margin-left: 0.5rem;
                padding: 0.05rem 0.4rem;
                border-radius: 999px;
                font-size: 0.65rem;
                font-weight: 700;
                letter-spacing: 0.03em;
                text-transform: uppercase;
                background: var(--p-primary-100, #e0e7ff);
                color: var(--p-primary-700, #4338ca);
                vertical-align: middle;
            }
        `
    ]
})
export class AppMenuitem implements OnDestroy {
    layoutService = inject(LayoutService);

    router = inject(Router);

    private readonly el = inject(ElementRef<HTMLElement>);

    private scrollTrack: HTMLElement | null = null;

    private menuScrollContainer: HTMLElement | null = null;

    private readonly onScrollReposition = () => this.repositionSubmenus();

    private readonly onResizeReposition = () => this.repositionSubmenus();

    item = input<any>(null);

    root = input<boolean>(true);

    level = input<number>(1);

    parentPath = input<string | null>(null);

    isDisabled = computed(() => this.item()?.disabled ?? false);

    isSlim = computed(() => this.layoutService.layoutConfig().menuMode === 'slim');

    isHorizontal = computed(() => this.layoutService.layoutConfig().menuMode === 'horizontal');

    isCompact = computed(() => this.layoutService.layoutConfig().menuMode === 'compact');

    isVisible = computed(() => this.item()?.visible !== false);

    hasChildren = computed(() => this.item()?.items && this.item()?.items.length > 0);

    hasCommand = computed(() => typeof this.item()?.command === 'function');

    hasRouterLink = computed(() => !!this.item()?.routerLink && !this.isDisabled());

    isHorizontalFlyout = computed(() => this.level() >= 2 && this.hasChildren());

    fullPath = computed(() => {
        const itemPath = this.item()?.path;
        if (!itemPath) return this.parentPath();
        const parent = this.parentPath();
        if (parent && !itemPath.startsWith(parent)) {
            return parent + itemPath;
        }
        return itemPath;
    });

    menuHoverActive = computed(() => this.layoutService.layoutState().menuHoverActive);

    isActive = computed(() => {
        const activePath = this.layoutService.layoutState().activePath;
        if (this.item()?.path) {
            return activePath?.startsWith(this.fullPath() ?? '') ?? false;
        }
        return false;
    });

    initialized = signal<boolean>(false);

    submenuEnterAnimation = computed(() => {
        if (this.isHorizontalFlyout()) {
            return null;
        }
        if (this.root() && this.isHorizontal() && this.layoutService.isDesktop()) {
            return null;
        }
        return this.initialized() ? 'p-submenu-enter' : null;
    });

    submenuLeaveAnimation = computed(() => {
        if (this.isHorizontalFlyout()) {
            return null;
        }
        if (this.root() && this.isHorizontal() && this.layoutService.isDesktop()) {
            return null;
        }
        return 'p-submenu-leave';
    });

    constructor() {
        effect(() => {
            this.updateActivePath();
        });

        effect(() => {
            const shouldPositionRoot =
                this.root() && this.isHorizontal() && this.layoutService.isDesktop() && this.hasChildren() && this.isActive();
            const shouldPositionFlyout = this.isHorizontalFlyout() && this.isActive();

            if (shouldPositionRoot || shouldPositionFlyout) {
                queueMicrotask(() => this.repositionSubmenus());
                this.bindRepositionListeners();
            } else if (
                (this.root() && this.isHorizontal() && this.layoutService.isDesktop()) ||
                this.isHorizontalFlyout()
            ) {
                this.resetFlyoutSubmenu();
                this.unbindRepositionListeners();
            }
        });
    }

    ngOnDestroy(): void {
        this.unbindRepositionListeners();
    }

    private bindRepositionListeners(): void {
        if (this.scrollTrack || this.menuScrollContainer) {
            return;
        }

        this.scrollTrack = this.el.nativeElement.closest('.layout-menu-viewport-track');
        this.menuScrollContainer = this.el.nativeElement.closest('.layout-menu-container');
        this.scrollTrack?.addEventListener('scroll', this.onScrollReposition, { passive: true });
        this.menuScrollContainer?.addEventListener('scroll', this.onScrollReposition, { passive: true });
        window.addEventListener('resize', this.onResizeReposition, { passive: true });
    }

    private unbindRepositionListeners(): void {
        this.scrollTrack?.removeEventListener('scroll', this.onScrollReposition);
        this.menuScrollContainer?.removeEventListener('scroll', this.onScrollReposition);
        this.scrollTrack = null;
        this.menuScrollContainer = null;
        window.removeEventListener('resize', this.onResizeReposition);
    }

    private repositionSubmenus(): void {
        if (this.isHorizontalFlyout() && this.isActive()) {
            this.positionFlyoutSubmenu();
        }
        if (this.root() && this.isHorizontal() && this.layoutService.isDesktop() && this.isActive()) {
            this.positionRootSubmenu();
        }
    }

    private positionRootSubmenu(): void {
        if (!this.root() || !this.isHorizontal() || !this.layoutService.isDesktop() || !this.isActive() || !this.hasChildren()) {
            return;
        }

        const host = this.el.nativeElement;
        const anchor = host.querySelector(':scope > a');
        const submenu = host.querySelector(':scope > ul') as HTMLElement | null;

        if (!(anchor instanceof HTMLElement) || !submenu) {
            return;
        }

        const rect = anchor.getBoundingClientRect();
        submenu.style.position = 'fixed';
        submenu.style.top = `${rect.bottom + 6}px`;
        submenu.style.left = `${rect.left}px`;
        submenu.style.minWidth = `${Math.max(rect.width, 240)}px`;
        submenu.style.transform = 'none';
        submenu.style.zIndex = '9999';
        submenu.style.visibility = 'visible';
        submenu.style.pointerEvents = 'auto';
    }

    private positionFlyoutSubmenu(): void {
        if (!this.isHorizontalFlyout() || !this.isActive() || !this.hasChildren()) {
            return;
        }

        const host = this.el.nativeElement;
        const anchor = host.querySelector(':scope > a');
        const submenu = host.querySelector(':scope > ul.layout-horizontal-submenulist') as HTMLElement | null;

        if (!(anchor instanceof HTMLElement) || !submenu) {
            return;
        }

        const rect = anchor.getBoundingClientRect();
        const gap = 6;
        submenu.style.position = 'fixed';
        submenu.style.top = `${rect.top}px`;
        submenu.style.left = `${rect.right + gap}px`;
        submenu.style.minWidth = `${Math.max(rect.width, 14 * 16)}px`;
        submenu.style.transform = 'none';
        submenu.style.zIndex = '10000';
        submenu.style.maxHeight = 'none';
        submenu.style.visibility = 'visible';
        submenu.style.pointerEvents = 'auto';
    }

    private resetFlyoutSubmenu(): void {
        if (!this.isHorizontalFlyout()) {
            return;
        }

        const submenu = this.el.nativeElement.querySelector(':scope > ul.layout-horizontal-submenulist') as HTMLElement | null;
        submenu?.removeAttribute('style');
    }

    updateActivePath() {
        // Don't automatically set activePath for overlay submenu modes (slim, horizontal, compact)
        // It should only be set through user interaction (hover, click)
        if (this.layoutService.hasOverlaySubmenu() && this.layoutService.isDesktop()) {
            return;
        }

        const item = this.item();
        const parentPath = this.parentPath();

        if (item?.routerLink && !item?.items) {
            const isRouteActive = this.router.isActive(item.routerLink[0], {
                paths: 'exact',
                queryParams: 'ignored',
                matrixParams: 'ignored',
                fragment: 'ignored'
            });

            if (isRouteActive && parentPath) {
                this.layoutService.layoutState.update((val) => ({
                    ...val,
                    activePath: parentPath
                }));
            }
        }
    }

    ngAfterViewInit() {
        setTimeout(() => {
            this.initialized.set(true);
        });
    }

    itemClick(event: Event) {
        if (this.isDisabled()) {
            event.preventDefault();
            event.stopPropagation();
            return;
        }

        if (this.hasChildren()) {
            event.preventDefault();
        }

        if (this.hasCommand()) {
            this.item().command({ originalEvent: event, item: this.item() });
        }

        if (this.hasChildren()) {
            if (this.isActive()) {
                const deactivateHover = this.root() && this.layoutService.hasOverlaySubmenu() && this.layoutService.isDesktop();
                this.layoutService.layoutState.update((val) => ({
                    ...val,
                    activePath: this.parentPath(),
                    menuHoverActive: deactivateHover ? false : val.menuHoverActive
                }));
            } else {
                this.layoutService.layoutState.update((val) => ({
                    ...val,
                    activePath: this.fullPath(),
                    menuHoverActive: true
                }));
                queueMicrotask(() => this.repositionSubmenus());
            }
        } else {
            this.layoutService.layoutState.update((val) => ({
                ...val,
                overlayMenuActive: false,
                mobileMenuActive: false,
                menuHoverActive: false
            }));

            if (this.layoutService.hasOverlaySubmenu() && this.layoutService.isDesktop()) {
                this.layoutService.layoutState.update((val) => ({
                    ...val,
                    activePath: null
                }));
            }
        }
    }

    onMouseEnter() {
        if (this.isDisabled() || !this.layoutService.isDesktop() || !this.hasChildren()) {
            return;
        }

        if (this.root() && this.menuHoverActive() && !this.isActive()) {
            this.layoutService.layoutState.update((val) => ({
                ...val,
                activePath: this.fullPath(),
                menuHoverActive: true
            }));
            queueMicrotask(() => this.repositionSubmenus());
            return;
        }

        if (this.isHorizontalFlyout() && !this.isActive()) {
            this.layoutService.layoutState.update((val) => ({
                ...val,
                activePath: this.fullPath(),
                menuHoverActive: true
            }));
            queueMicrotask(() => this.repositionSubmenus());
        }
    }
}
